'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge } from '@/comps/primitives/badge';
import { Button } from '@/comps/primitives/button';
import { InfoTooltip } from '@/comps/info-tooltip';
import { AiProficiencyReportCard } from '@/domains/pages/ai-proficiency/components/ai-proficiency-report-card';
import { MODEL_SAVINGS_KPI_NUMBER_CLASS, MODEL_SAVINGS_KPI_SUFFIX_CLASS, MODEL_SAVINGS_POSITIVE_COLOR } from '@/domains/pages/model-savings/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { SavingsSharePieChart } from '@/domains/pages/model-savings/components/savings-share-pie-chart';
import type { SavingsScopeData } from '@/domains/pages/model-savings/types';
import { formatAnnualSavings, formatAnnualSpend } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { cn } from '@/utils/shad';
import { ArrowRight } from 'lucide-react';
const SAVINGS_OPPORTUNITY_METHOD_TOOLTIP = 'Based on tasks completed in the last 30 days, each model used is compared with the lowest-cost model tier that could complete the same task at similar quality. The difference in AI spend is annualized to estimate potential yearly savings.';
type SavingsOpportunityCardProps = {
  data: Pick<SavingsScopeData, 'savingAnnual' | 'spendAnnual' | 'savingSharePercent'>;
  /** The team, market or tenure the figures are scoped to; absent on the org-wide view. */
  segmentName?: string;
  onCheckDetails: () => void;
};
export function SavingsOpportunityCard(t0) {
  const $ = _c(31);
  const {
    data,
    segmentName,
    onCheckDetails
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ArrowRight className="size-3" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onCheckDetails) {
    t2 = <Button variant="outline" size="sm" className="h-7 gap-1.5 rounded-full px-3 text-xs text-muted-foreground hover:text-foreground" onClick={onCheckDetails}>View details{t1}</Button>;
    $[1] = onCheckDetails;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== data.savingAnnual || $[4] !== data.savingSharePercent || $[5] !== data.spendAnnual) {
    t3 = <SavingsSharePieChart percent={data.savingSharePercent} size={136} savingAnnual={data.savingAnnual} spendAnnual={data.spendAnnual} />;
    $[3] = data.savingAnnual;
    $[4] = data.savingSharePercent;
    $[5] = data.spendAnnual;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn(MODEL_SAVINGS_KPI_NUMBER_CLASS, AIFI_DATA_NUMBER_CLASS);
    t5 = {
      color: MODEL_SAVINGS_POSITIVE_COLOR
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== data.savingAnnual) {
    t6 = formatAnnualSavings(data.savingAnnual);
    $[9] = data.savingAnnual;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className={MODEL_SAVINGS_KPI_SUFFIX_CLASS}>/Y</span>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6) {
    t8 = <p className={t4} style={t5}>{t6}{t7}</p>;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== data.spendAnnual) {
    t9 = formatAnnualSpend(data.spendAnnual);
    $[14] = data.spendAnnual;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <p className="mt-2 text-sm text-muted-foreground">of <span className={AIFI_DATA_NUMBER_CLASS}>{t9}</span> / Y actual AI spend</p>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== segmentName) {
    t11 = segmentName ? <Badge variant="outline" pill={true} className="mt-2 max-w-full border-border bg-transparent px-2 py-0.5 text-[11px] font-medium text-white"><span className="truncate">{segmentName}</span></Badge> : null;
    $[18] = segmentName;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t8) {
    t12 = <div className="min-w-0 flex-1">{t8}{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t8;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t3) {
    t13 = <div className="flex items-center gap-8">{t3}{t12}</div>;
    $[24] = t12;
    $[25] = t3;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="mt-4 text-sm leading-snug text-muted-foreground">Potential annualized savings if every task used the most cost-efficient model tier<InfoTooltip className="inline-flex align-middle" content={SAVINGS_OPPORTUNITY_METHOD_TOOLTIP} label="How model savings opportunity is calculated" /></p>;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t2) {
    t15 = <AiProficiencyReportCard title="Model savings" description="Total savings potential for the selected scope, shown against actual AI spend and as a share of it." className="min-h-0 flex-1" contentClassName="flex min-h-0 flex-1 flex-col justify-center !py-4" headerTrailing={t2}>{t13}{t14}</AiProficiencyReportCard>;
    $[28] = t13;
    $[29] = t2;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}